import { useCallback, useState } from 'react';
import { FileText, Layers, FileStack, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

export const REPARSE_TYPES = [
  { value: 'single', label: 'As Single Bill', hint: 'One account, standard single-bill parse', icon: FileText },
  { value: 'collective', label: 'As Collective Singles', hint: 'Independent accounts, one single bill each', icon: Layers },
  { value: 'consolidated', label: 'As Consolidated', hint: 'Master bill with sub-accounts', icon: FileStack },
];

export const isBillValid = (doc) =>
  doc?.validation_status === 'passed' || doc?.parse_health?.ok === true;

export const reparseToast = (rp) =>
  rp?.processing_method === 'postprocess'
    ? `${rp.message} (fixed by post-processing, no AI credits used)`
    : null;

export const ReparseTypeItems = ({ onPick, testIdPrefix = 'reparse-as' }) => (
  <>
    <DropdownMenuSeparator />
    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
      Re-parse as
    </DropdownMenuLabel>
    {REPARSE_TYPES.map(({ value, label, hint, icon: Icon }) => (
      <DropdownMenuItem
        key={value}
        onClick={() => onPick(value)}
        className="cursor-pointer items-start"
        data-testid={`${testIdPrefix}-${value}`}
      >
        <Icon className="h-4 w-4 mr-2 mt-0.5 shrink-0" />
        <div className="flex flex-col">
          <span>{label}</span>
          <span className="text-[10px] text-slate-500">{hint}</span>
        </div>
      </DropdownMenuItem>
    ))}
  </>
);

export const RowReparseMenu = ({ doc, onReparse, showLabel = false }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        data-testid={`reparse-document-${doc.id}`}
        size="sm"
        variant="outline"
        className={showLabel ? 'rounded-none h-7 px-2 text-xs' : 'rounded-none h-7 w-7 p-0'}
        title="Re-parse document"
      >
        <RefreshCw className={showLabel ? 'h-3 w-3 mr-1' : 'h-3 w-3'} />
        {showLabel && 'Reparse'}
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="rounded-none w-64">
      <DropdownMenuItem
        onClick={() => onReparse(doc, null)}
        className="cursor-pointer"
        data-testid={`reparse-auto-${doc.id}`}
      >
        <RefreshCw className="h-4 w-4 mr-2" />
        Re-parse (auto-detect type)
      </DropdownMenuItem>
      <ReparseTypeItems onPick={(t) => onReparse(doc, t)} testIdPrefix={`reparse-${doc.id}-as`} />
    </DropdownMenuContent>
  </DropdownMenu>
);

export const useReparseConfirm = () => {
  const [pending, setPending] = useState(null);
  const confirm = useCallback((doc, run) => {
    if (isBillValid(doc)) setPending(() => run);
    else run();
  }, []);
  const dialog = (
    <AlertDialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
      <AlertDialogContent className="rounded-none" data-testid="reparse-valid-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            This bill looks valid
          </AlertDialogTitle>
          <AlertDialogDescription>
            Its line items and total amount due already reconcile. Re-parsing may replace the
            current result. Do you really want to re-parse it?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none" data-testid="reparse-valid-cancel-btn">
            Keep current result
          </AlertDialogCancel>
          <AlertDialogAction
            className="rounded-none"
            data-testid="reparse-valid-confirm-btn"
            onClick={() => { const run = pending; setPending(null); run && run(); }}
          >
            Re-parse anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return { confirm, dialog };
};
